/**
 * CLARTEXT · A1 (Feb 2026) — Aushangpflicht-Anzeige · gemeinsame Anzeige-Komponente.
 *
 * Rein listende Darstellung des vom Server ausgelieferten Kategorie-/Eintrags-
 * Objekts (`/me/aushang`, `/owner/aushang`). Keine Bearbeitung, kein Fetch,
 * kein State. Wird sowohl auf der Mitarbeiterseite als auch auf der Owner-
 * Seite verwendet.
 *
 * i18n (Juni 2026): Bedienoberflächen-Texte laufen über das Monteur-Dictionary.
 * Der Owner-Kontext übergibt `forcedLang="de"` (useResolvedT-Muster), damit
 * eine im selben Browser aktive Monteur-Session die Owner-UI nicht übersetzt.
 * Die amtlichen Gesetzestitel, Kurzbeschreibungen und Links selbst sind
 * Server-Daten und bleiben unverändert deutsch.
 */
import { ExternalLink } from "lucide-react";
import { useResolvedT } from "@/i18n/monteur";

// Kategorie-Überschriften: reiner Anzeige-Lookup per Server-Key. Unbekannte
// Kategorien fallen auf das (deutsche) Server-Label zurück.
const CATEGORY_LABEL_KEYS = {
  allgemein: "aushang.categoryAllgemein",
  dguv_technische_regeln: "aushang.categoryDguv",
};

export function AushangList({ data, forcedLang }) {
  const t = useResolvedT(forcedLang);

  if (!data) {
    return (
      <div className="text-sm text-slate-500" data-testid="aushang-list-loading">
        {t("aushang.listLoading")}
      </div>
    );
  }
  const kategorien = data.kategorien || [];
  const dguvHinweis = data.dguv_hinweis || "";

  if (kategorien.length === 0) {
    return (
      <div
        className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-6 text-center"
        data-testid="aushang-list-empty"
      >
        {t("aushang.listEmpty")}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="aushang-list">
      {kategorien.map((k) => (
        <section
          key={k.key}
          data-testid={`aushang-category-${k.key}`}
          className="space-y-3"
        >
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              {CATEGORY_LABEL_KEYS[k.key] ? t(CATEGORY_LABEL_KEYS[k.key]) : k.label}
            </h2>
            <span className="text-xs text-slate-400 tabular-nums">
              {k.eintraege.length}
            </span>
          </div>
          {k.key === "dguv_technische_regeln" && dguvHinweis && (
            <p
              className="text-sm text-slate-500 italic"
              data-testid="aushang-dguv-hinweis"
            >
              {dguvHinweis}
            </p>
          )}
          {k.eintraege.length === 0 ? (
            <p
              className="text-sm text-slate-400"
              data-testid={`aushang-category-empty-${k.key}`}
            >
              {t("aushang.categoryEmpty")}
            </p>
          ) : (
            <ol className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
              {k.eintraege.map((e) => (
                <li
                  key={`${k.key}-${e.sort_order}-${e.titel}`}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                  data-testid={`aushang-entry-${k.key}-${e.sort_order}`}
                >
                  <span className="text-xs font-medium text-slate-400 tabular-nums pt-0.5 min-w-6">
                    {e.sort_order}.
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 leading-snug">
                      {e.titel}
                    </div>
                    {e.kurzbeschreibung ? (
                      <div className="text-xs text-slate-500 mt-0.5">
                        {e.kurzbeschreibung}
                      </div>
                    ) : null}
                    <a
                      href={e.display_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-600 hover:text-slate-900 hover:underline mt-1 inline-flex items-center gap-1"
                      data-testid={`aushang-link-${k.key}-${e.sort_order}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="break-all">{e.display_url}</span>
                    </a>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      ))}
    </div>
  );
}

export default AushangList;
